import { useState, useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { useNavigate } from 'react-router-dom';
import { useBatch } from '@/contexts/BatchContext';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { useToast } from '@/components/ui/use-toast';
import { Class } from '@/types';
import { classApi } from '@/lib/api';

interface ClassFormProps {
  classData?: Class;
  isEditing?: boolean;
}

const GRADE_LEVELS = [
  '8th grade',
  '9th grade',
  '10th grade',
  '11th grade',
  '12th grade',
  'None',
];

const ClassForm = ({ classData, isEditing = false }: ClassFormProps) => {
  const { toast } = useToast();
  const navigate = useNavigate();
  const { currentBatch, batches } = useBatch();
  const [isSubmitting, setIsSubmitting] = useState(false);

  const { register, handleSubmit, setValue, watch, formState: { errors } } = useForm<Class>({
    defaultValues: classData || {
      name: '',
      description: '',
      grade: '',
      section: '',
      batchId: currentBatch?.id || '',
      studentIds: [],
      testIds: [],
    }
  });

  // Register the grade field with validation
  useEffect(() => {
    register('grade', { required: 'Grade level is required' });
    register('section', { required: 'Section is required' });
    register('batchId', { required: 'Batch is required' });
  }, [register]);

  // Set the batch when currentBatch changes
  useEffect(() => {
    if (!isEditing && currentBatch && !watch('batchId')) {
      setValue('batchId', currentBatch.id);
    }
  }, [currentBatch, setValue, isEditing, watch]);

  const onSubmit = async (data: Class) => {
    try {
      setIsSubmitting(true);
      console.log('Form submitted with data:', data);

      // Validate required fields
      if (!data.name) {
        console.error('Class name is required');
        toast({
          title: 'Error',
          description: 'Class name is required',
          variant: 'destructive',
        });
        return;
      }

      if (!data.grade) {
        console.error('Grade level is required');
        toast({
          title: 'Error',
          description: 'Grade level is required',
          variant: 'destructive',
        });
        return;
      }

      if (!data.section) {
        console.error('Section is required');
        toast({
          title: 'Error',
          description: 'Section is required',
          variant: 'destructive',
        });
        return;
      }

      if (!data.batchId) {
        console.error('Batch is required');
        toast({
          title: 'Error',
          description: 'Batch is required',
          variant: 'destructive',
        });
        return;
      }

      // Format the data to match the backend model
      const formattedData = {
        ...data,
        studentIds: data.studentIds || [],
        testIds: data.testIds || [],
      };

      console.log('Submitting class data:', formattedData);

      if (isEditing && classData?.id) {
        // Update existing class
        console.log('Updating class with ID:', classData.id);
        const updatedClass = await classApi.update(classData.id, formattedData);
        console.log('Class updated successfully:', updatedClass);
        toast({
          title: 'Success',
          description: 'Class updated successfully',
        });
      } else {
        // Create new class
        console.log('Creating new class');
        const newClass = await classApi.create(formattedData);
        console.log('Class created successfully:', newClass);
        toast({
          title: 'Success',
          description: 'Class created successfully',
        });
      }

      // Navigate back to classes page
      navigate('/classes');
    } catch (error) {
      console.error('Error submitting class:', error);
      toast({
        title: 'Error',
        description: 'Failed to save class. Please try again.',
        variant: 'destructive',
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  console.log('Rendering ClassForm with props:', { classData, isEditing, currentBatch, batches });

  return (
    <form onSubmit={(e) => {
      console.log('Form submitted, calling handleSubmit');
      return handleSubmit(onSubmit)(e);
    }} className="space-y-6">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <div className="space-y-2">
          <Label htmlFor="name">Class Name</Label>
          <Input
            id="name"
            {...register('name', { required: 'Class name is required' })}
          />
          {errors.name && (
            <p className="text-sm text-destructive">{errors.name.message}</p>
          )}
        </div>

        <div className="space-y-2">
          <Label htmlFor="grade">Grade Level</Label>
          <Select
            onValueChange={(value) => {
              setValue('grade', value);
              console.log('Grade selected:', value);
            }}
            defaultValue={watch('grade')}
          >
            <SelectTrigger id="grade">
              <SelectValue placeholder="Select grade level" />
            </SelectTrigger>
            <SelectContent>
              {GRADE_LEVELS.map((grade) => (
                <SelectItem key={grade} value={grade}>
                  {grade}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          {errors.grade && (
            <p className="text-sm text-destructive">{errors.grade.message}</p>
          )}
        </div>

        <div className="space-y-2">
          <Label htmlFor="section">Section</Label>
          <Input
            id="section"
            {...register('section', { required: 'Section is required' })}
            placeholder="e.g., A, B, Science, Commerce"
          />
          {errors.section && (
            <p className="text-sm text-destructive">{errors.section.message}</p>
          )}
        </div>
      </div>

      <div className="space-y-2">
        <Label htmlFor="batchId">Batch</Label>
        <Select
          onValueChange={(value) => {
            setValue('batchId', value);
            console.log('Batch selected:', value);
          }}
          defaultValue={watch('batchId')}
        >
          <SelectTrigger id="batchId">
            <SelectValue placeholder="Select batch" />
          </SelectTrigger>
          <SelectContent>
            {batches.map((batch) => (
              <SelectItem key={batch.id} value={batch.id}>
                {batch.year}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        {errors.batchId && (
          <p className="text-sm text-destructive">{errors.batchId.message}</p>
        )}
      </div>

      <div className="space-y-2">
        <Label htmlFor="description">Description</Label>
        <Textarea
          id="description"
          {...register('description')}
          rows={4}
        />
      </div>

      <div className="flex justify-end space-x-4">
        <Button
          type="button"
          variant="outline"
          onClick={() => navigate('/classes')}
          disabled={isSubmitting}
        >
          Cancel
        </Button>
        <Button type="submit" disabled={isSubmitting}>
          {isSubmitting ? (
            <>
              <span className="mr-2">
                {isEditing ? 'Updating class...' : 'Creating class...'}
              </span>
              <span className="animate-spin">⏳</span>
            </>
          ) : (
            isEditing ? 'Update Class' : 'Create Class'
          )}
        </Button>
      </div>
    </form>
  );
};

export default ClassForm;
